import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft, 
  Check, 
  Crown,
  MessageSquare,
  TrendingUp,
  Shield,
  Clock,
  X,
  Lock,
  Unlock,
  Newspaper,
  Zap,
  BarChart3,
  CreditCard,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("token");
const LOGO_URL = "/doslions-logo.png";

export default function Subscription() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [squareLoaded, setSquareLoaded] = useState(false);
  const [card, setCard] = useState(null);
  const cardContainerRef = useRef(null);

  // Pro Ecopreneur Features
  const proFeatures = [
    { icon: <Zap className="w-5 h-5" />, text: "AI Hot Picks — Daily BUY/SELL Signals" },
    { icon: <TrendingUp className="w-5 h-5" />, text: "Top Market Movers & AI Whale Tracker" },
    { icon: <BarChart3 className="w-5 h-5" />, text: "AI Earnings Whisper & Sentiment AI" },
    { icon: <Newspaper className="w-5 h-5" />, text: "Economic Calendar AI & Weekly Reports" },
    { icon: <Clock className="w-5 h-5" />, text: "500+ Assets: Stocks, Crypto, Commodities" },
    { icon: <Shield className="w-5 h-5" />, text: "Priority Support" }
  ];

  const comparison = [
    { feature: "Basic Stock/Crypto Prices", free: true, pro: true },
    { feature: "Personal Watchlist", free: true, pro: true },
    { feature: "Market Search", free: true, pro: true },
    { feature: "AI Hot Picks — Daily Signals", free: false, pro: true },
    { feature: "Top Movers & Whale Tracker", free: false, pro: true },
    { feature: "AI Earnings Whisper", free: false, pro: true },
    { feature: "Social Sentiment AI", free: false, pro: true },
    { feature: "Economic Calendar AI", free: false, pro: true },
    { feature: "500+ Extended Market Data", free: false, pro: true },
    { feature: "Priority Support", free: false, pro: true }
  ];

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/subscription/config`);
        setConfig(response.data);
        
        // Load Square Web Payments SDK
        if (response.data.application_id) {
          loadSquareSDK(response.data);
        }
      } catch (error) {
        toast.error("Failed to load subscription config");
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const loadSquareSDK = async (configData) => {
    // Check if script already loaded
    if (window.Square) {
      initializeSquarePayment(configData);
      return;
    }

    const script = document.createElement("script");
    script.src = configData.environment === "sandbox" 
      ? "https://sandbox.web.squarecdn.com/v1/square.js"
      : "https://web.squarecdn.com/v1/square.js";
    script.onload = () => initializeSquarePayment(configData);
    script.onerror = () => toast.error("Failed to load payment system");
    document.body.appendChild(script);
  };

  const initializeSquarePayment = async (configData) => {
    try {
      if (!window.Square) {
        console.error("Square SDK not loaded");
        return;
      }
      // Prevent double-initialization
      if (card) return;

      const payments = window.Square.payments(configData.application_id, configData.location_id);
      const cardInstance = await payments.card();
      
      if (cardContainerRef.current) {
        // Clear any previous renders
        cardContainerRef.current.innerHTML = '';
        await cardInstance.attach(cardContainerRef.current);
        setCard(cardInstance);
        setSquareLoaded(true);
      }
    } catch (error) {
      console.error("Square initialization error:", error);
      toast.error("Payment system initialization failed");
    }
  };

  const handleSquarePayment = async () => {
    if (!card) {
      toast.error("Payment form not ready. Please refresh the page.");
      return;
    }

    setProcessing(true);
    try {
      // Tokenize the card
      const result = await card.tokenize();
      
      if (result.status === "OK") {
        // Send to backend for processing
        const response = await axios.post(
          `${API}/subscription/checkout`,
          { source_id: result.token },
          { headers: { Authorization: `Bearer ${getToken()}` }}
        );

        if (response.data.success) {
          updateUser({ 
            ...user, 
            is_subscribed: true, 
            subscription_tier: "pro",
            subscription_end: response.data.subscription_end
          });
          toast.success(response.data.message || "Welcome to Pro Ecopreneur!");
          navigate("/dashboard");
        }
      } else {
        const errorMessage = result.errors?.[0]?.message || "Payment failed. Please try again.";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Payment error:", error);
      const errorMsg = error.response?.data?.detail || "Payment processing failed. Please try again.";
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription?")) return;
    
    try {
      await axios.post(
        `${API}/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` }}
      );
      updateUser({ ...user, is_subscribed: false, subscription_tier: "free" });
      toast.success("Subscription cancelled. You can resubscribe anytime.");
    } catch (error) {
      toast.error("Failed to cancel subscription");
    }
  };

  const isPro = user?.subscription_tier === "pro" && user?.is_subscribed;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="text-zinc-400 hover:text-amber-500"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div 
                className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate("/dashboard")}
                data-testid="logo-home-link"
              >
                <img src={LOGO_URL} alt="ECOLIONS" className="w-10 h-10 object-contain" />
                <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                  <span className="text-amber-500">ECOLIONS</span> AI
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {isPro ? (
          /* Already Subscribed View */
          <div className="bg-[#111111] border border-amber-500/30 p-8 text-center shadow-[0_0_40px_rgba(245,158,11,0.1)]">
            <div className="w-16 h-16 bg-amber-500/20 mx-auto flex items-center justify-center mb-6">
              <Crown className="w-8 h-8 text-amber-500" />
            </div>
            <h1 
              className="text-4xl font-bold tracking-tight uppercase mb-2"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              You're a <span className="text-amber-500">Pro Ecopreneur</span>!
            </h1>
            <p className="text-zinc-400 mb-8">
              Enjoy full access to all premium features.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
              {proFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 text-left p-3 bg-[#0A0A0A] border border-zinc-800">
                  <div className="text-amber-500">{feature.icon}</div>
                  <span className="text-sm">{feature.text}</span>
                </div>
              ))}
            </div>

            {user?.subscription_end && (
              <p className="text-sm text-zinc-500 mb-6">
                Next billing: {new Date(user.subscription_end).toLocaleDateString()}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate("/dashboard")}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider"
                data-testid="open-dashboard-btn"
              >
                Go to Dashboard
              </Button>
              <Button 
                variant="outline"
                onClick={handleCancel}
                className="border-zinc-700 text-zinc-400 hover:text-white hover:border-red-500"
                data-testid="cancel-subscription-btn"
              >
                Cancel Subscription
              </Button>
            </div>
          </div>
        ) : (
          /* Subscription Options */
          <div className="space-y-8">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 mb-6">
                <Lock className="w-4 h-4 text-amber-500" />
                <span className="text-xs tracking-[0.2em] text-amber-500 font-bold uppercase">
                  Unlock Pro Ecopreneur Features
                </span>
              </div>
              <h1 
                className="text-5xl font-bold tracking-tight uppercase mb-4"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                Go <span className="text-amber-500">Pro Ecopreneur</span>
              </h1>
              <p className="text-zinc-400 max-w-lg mx-auto">
                Get full access to 6 AI engines: Hot Picks, Whale Tracker, Earnings Whisper, Social Sentiment, Economic Calendar, and 500+ assets.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Free Plan */}
              <div className="bg-[#111111] border border-zinc-800 p-6">
                <p className="text-xs tracking-[0.3em] text-zinc-500 font-bold uppercase mb-4">
                  Free Plan
                </p>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-bold font-data">$0</span>
                  <span className="text-zinc-500">/forever</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {comparison.map((item, index) => (
                    <li key={index} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400">{item.feature}</span>
                      {item.free ? (
                        <Check className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <X className="w-4 h-4 text-zinc-600" />
                      )}
                    </li>
                  ))}
                </ul>
                <Button 
                  variant="outline" 
                  className="w-full border-zinc-700"
                  disabled
                >
                  Current Plan
                </Button>
              </div>

              {/* Pro Ecopreneur Plan */}
              <div className="bg-[#111111] border-2 border-amber-500 p-6 relative shadow-[0_0_40px_rgba(245,158,11,0.15)]">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-black text-xs font-bold px-4 py-1 uppercase tracking-wider">
                  Most Popular
                </div>
                <p className="text-xs tracking-[0.3em] text-amber-500 font-bold uppercase mb-4">
                  Pro Ecopreneur
                </p>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-bold font-data text-amber-500">$19.97</span>
                  <span className="text-zinc-500">/first month</span>
                </div>
                <p className="text-sm text-zinc-400 mb-6">Then $49.97/month</p>
                
                <ul className="space-y-3 mb-6">
                  {comparison.map((item, index) => (
                    <li key={index} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-300">{item.feature}</span>
                      {item.pro ? (
                        <Check className="w-4 h-4 text-amber-500" />
                      ) : (
                        <X className="w-4 h-4 text-zinc-600" />
                      )}
                    </li>
                  ))}
                </ul>

                {loading ? (
                  <div className="h-24 bg-zinc-800 animate-pulse rounded"></div>
                ) : (
                  <div className="space-y-4">
                    {/* Square Card Form */}
                    <div className="bg-[#1a1a1a] border border-zinc-700 rounded p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <CreditCard className="w-4 h-4 text-zinc-400" />
                        <span className="text-sm text-zinc-400">Card Details</span>
                      </div>
                      <div 
                        ref={cardContainerRef} 
                        id="card-container"
                        className="min-h-[50px]"
                      ></div>
                      {!squareLoaded && (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-5 h-5 text-amber-500 animate-spin mr-2" />
                          <span className="text-sm text-zinc-500">Loading payment form...</span>
                        </div>
                      )}
                    </div>
                    
                    <Button 
                      className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider py-6"
                      onClick={handleSquarePayment}
                      disabled={!squareLoaded || processing}
                      data-testid="pay-btn"
                    >
                      {processing ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Unlock className="w-5 h-5 mr-2" />
                          Pay $19.97 - Start Pro Ecopreneur
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                <p className="text-xs text-zinc-500 text-center mt-4">
                  $19.97 first month, then $49.97/mo. Cancel anytime.
                </p>
                
                <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-zinc-800">
                  <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/visa.svg" alt="Visa" className="h-6 opacity-50 invert" />
                  <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/mastercard.svg" alt="Mastercard" className="h-6 opacity-50 invert" />
                  <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/americanexpress.svg" alt="Amex" className="h-6 opacity-50 invert" />
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div className="bg-[#111111] border border-zinc-800 p-6">
              <h2 
                className="text-xl font-bold tracking-tight uppercase mb-4"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                <span className="text-amber-500">FAQ</span>
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-sm mb-1">What do I get with Pro Ecopreneur?</p>
                  <p className="text-sm text-zinc-400">Access to AI Hot Picks with daily BUY/SELL signals, Top Market Movers, AI Whale Tracker, Earnings Whisper, Social Sentiment AI, Economic Calendar, 500+ assets, and priority support.</p>
                </div>
                <div>
                  <p className="font-semibold text-sm mb-1">Can I cancel anytime?</p>
                  <p className="text-sm text-zinc-400">Yes, you can cancel your subscription at any time with no penalties.</p>
                </div>
                <div>
                  <p className="font-semibold text-sm mb-1">Is payment secure?</p>
                  <p className="text-sm text-zinc-400">All payments are securely processed through Square. We never store your card details.</p>
                </div>
                <div>
                  <p className="font-semibold text-sm mb-1">What happens after the trial?</p>
                  <p className="text-sm text-zinc-400">After your $19.97 first month, you'll be charged $49.97/month unless you cancel.</p>
                </div>
              </div>
            </div>

            {/* Trust Badge */}
            <div className="text-center">
              <p className="text-xs text-zinc-600">
                Secure payment powered by Square. This is a TIME-SAVING DATA SERVICE for EDUCATIONAL purposes only.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
